"use client";

import { ListSkeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useNotifications } from "@/hooks/use-notifications";
import { resolveNotificationLink } from "@/lib/notification-link";
import { cn } from "@/lib/utils";
import type { DbNotification } from "@/types/db";
import { BellIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";

const UNREAD_BADGE_CAP = 9;

// Compact relative timestamp for the dropdown ("now", "5m", "3h", "2d");
// older items fall back to a medium date.
const formatRelativeTime = (iso: string): string => {
  const elapsedMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(
    new Date(iso)
  );
};

type NotificationsBellProps = {
  userId: string | null;
  /** Sidebar expanded state — mirrors SidebarButton's expanded prop. */
  expanded?: boolean;
  /** Called after a navigation from the dropdown (e.g. to close a mobile Sheet). */
  onNavigate?: () => void;
};

export const NotificationsBell = ({
  userId,
  expanded = false,
  onNavigate,
}: NotificationsBellProps) => {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } =
    useNotifications(userId);

  const badgeLabel =
    unreadCount > UNREAD_BADGE_CAP ? `${UNREAD_BADGE_CAP}+` : String(unreadCount);

  const handleSelect = (notification: DbNotification) => {
    setOpen(false);
    if (!notification.read_at) {
      void markAsRead(notification.id);
    }
    const link = resolveNotificationLink(notification);
    if (link) {
      router.push(link);
      onNavigate?.();
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    router.push("/notifications");
    onNavigate?.();
  };

  const trigger = (
    <Button
      aria-label={
        unreadCount > 0
          ? `Notifications (${unreadCount} unread)`
          : "Notifications"
      }
      className={cn(
        "h-8 overflow-hidden transition-all duration-200 ease-in-out",
        expanded ? "w-full justify-start gap-2 px-2" : "w-8 justify-center gap-0 px-0"
      )}
      size="default"
      type="button"
      variant="ghost"
    >
      <span className="relative shrink-0">
        <BellIcon size={15} />
        {unreadCount > 0 ? (
          <span className="absolute -right-1.5 -top-1.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-0.5 text-[9px] font-semibold leading-none text-primary-foreground">
            {badgeLabel}
          </span>
        ) : null}
      </span>
      <span
        className={cn(
          "inline-block overflow-hidden whitespace-nowrap text-[13px] font-normal transition-[max-width,opacity] duration-200 ease-in-out",
          expanded ? "max-w-32 opacity-100" : "max-w-0 opacity-0"
        )}
      >
        Notifications
      </span>
    </Button>
  );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      {expanded ? (
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>Notifications</p>
          </TooltipContent>
        </Tooltip>
      )}

      <DropdownMenuContent align="start" side="right" className="w-80">
        <div className="flex items-center justify-between gap-2 px-2 py-1.5">
          <p className="text-xs font-medium text-muted-foreground">Notifications</p>
          {unreadCount > 0 ? (
            <Button
              className="h-6 px-2 text-xs text-muted-foreground"
              onClick={() => void markAllAsRead()}
              size="sm"
              type="button"
              variant="ghost"
            >
              Mark all read
            </Button>
          ) : null}
        </div>

        <DropdownMenuSeparator />

        {loading && notifications.length === 0 ? (
          <ListSkeleton rows={3} />
        ) : notifications.length === 0 ? (
          <p className="px-2 py-4 text-center text-sm text-muted-foreground">
            No notifications yet.
          </p>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className="flex items-start gap-2 py-2"
                onClick={() => handleSelect(notification)}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "mt-1.5 size-1.5 shrink-0 rounded-full",
                    notification.read_at ? "bg-transparent" : "bg-primary"
                  )}
                />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span
                    className={cn(
                      "truncate text-sm",
                      notification.read_at ? "text-muted-foreground" : "font-medium"
                    )}
                  >
                    {notification.title}
                  </span>
                  {notification.body ? (
                    <span className="line-clamp-2 text-xs text-muted-foreground">
                      {notification.body}
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  {formatRelativeTime(notification.created_at)}
                </span>
              </DropdownMenuItem>
            ))}
          </div>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="justify-center text-sm text-muted-foreground"
          onClick={handleViewAll}
        >
          View all notifications
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
